/**
 * Cross-tenant provenance — the single place that answers "is this ours?".
 *
 * Per the FLYsafe MQTT Topic doc (§Partnership), partner aircraft do NOT get
 * their own topic root. A partner tenant's telemetry / networkid / OI
 * declaration lands on the *same* per-aircraft `v1/output/aircraft/{sn}/…`
 * topics as our own fleet, on our own broker, written there by the backend's
 * `partner-{B}-to-{A}-mqtt-sink` KafkaConnector. The ONLY thing that
 * distinguishes them is a payload field the connector injects:
 *
 *   - `tenantId`     — the receiving tenant (us). Constant per connection.
 *   - `sourceTenant` — the originating tenant. Equal to `tenantId` for our
 *                      own fleet; different for a partner's aircraft.
 *
 * So provenance is decided by payload inspection, never by topic parsing —
 * which is why this module takes a payload (or a bare tenant string) rather
 * than a topic. Everything downstream (grey marker, `Partner: {tenant}`
 * label, fleet-list External section, Overview Partner section, event badges)
 * keys off the result.
 *
 * Deliberately conservative: an absent or same-as-ours `sourceTenant` means
 * "ours". We never manufacture partner provenance from `tenantId` alone —
 * on sandbox today both fields read "sandbox", and a looser rule there would
 * paint the entire home fleet grey.
 */
import { config } from '@/lib/config';

function text(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  const t = v.trim();
  return t.length > 0 ? t : null;
}

const norm = (s: string | null) => s?.toLowerCase() ?? null;

/** Our own tenant id, as configured for this build/env (`VITE_TENANT_ID`). */
export function ownTenantId(): string {
  return config.tenant.id;
}

/**
 * Is `source` a tenant other than ours? Returns the tenant name verbatim
 * when it is, else null. Use for anything that already holds a resolved
 * `source_tenant` string (events, alerts) rather than a raw payload.
 */
export function foreignTenant(source: string | null | undefined): string | null {
  const s = text(source);
  if (!s) return null;
  return norm(s) === norm(ownTenantId()) ? null : s;
}

/**
 * Locate `sourceTenant` in an aircraft payload.
 *
 * It is NOT where the doc implies. Sniffed live 2026-08-13 off
 * `emqx-ws.sandbox.stage.flysafe.live`, aircraft SIMTL83:
 *
 *   v1/output/aircraft/SIMTL83/networkid → ext.sourceTenant = "airmarket"
 *   v1/output/aircraft/SIMTL83/telemetry → ext.sourceTenant = "airmarket"
 *
 * On BOTH topics it sits inside the `ext` block, alongside `callsign`,
 * `trackOrigin`, `pilotFullName` and the other AirMarket extensions — never
 * at the top level, which is where the doc's field table puts it. Reading
 * only the top level is why a partner aircraft rendered as own-fleet.
 *
 * Top-level and snake_case spellings are still accepted so a backend
 * correction to the documented shape doesn't silently switch partners back
 * off; `ext` is checked first because it is what actually ships today.
 */
function sourceTenantOf(p: Record<string, unknown>): string | null {
  const ext = (p.ext ?? {}) as Record<string, unknown>;
  return (
    text(ext.sourceTenant) ??
    text(ext.source_tenant) ??
    text(p.sourceTenant) ??
    text(p.source_tenant)
  );
}

/**
 * Partner tenant for a raw MQTT payload, or null when the aircraft is ours.
 *
 * The "us" reference is our OWN configured tenant, not the payload's — with
 * `tenantId` (camelCase, the doc's receiving-tenant field) accepted only as
 * a fallback when `VITE_TENANT_ID` is unset.
 *
 * Snake-case `tenant_id` is deliberately NOT treated as "us", even though
 * the parsers accept it elsewhere. Sniffed off the live sandbox broker
 * (2026-08-13, `emqx-ws.sandbox.stage.flysafe.live`, own tenant `sandbox`):
 *
 *   v1/output/events                  → { tenantId:  "sandbox"  }   ✓ us
 *   v1/output/aircraft/{sn}/telemetry → { tenant_id: "airmarket" }  ✗ NOT us
 *   v1/output/aircraft/{sn}/networkid → {}                          (absent)
 *
 * So the two spellings are not the same field. `tenant_id` on telemetry is
 * aircraft-ownership metadata (kept verbatim on the envelope's `tenant_id`),
 * and reading it as the receiving tenant would compare `sourceTenant` against
 * the wrong name — misclassifying our own correctly-stamped fleet as
 * partners and turning every marker grey.
 */
export function partnerTenantFromPayload(p: Record<string, unknown>): string | null {
  const source = sourceTenantOf(p);
  if (!source) return null;
  // `config.tenant.id` falls back to the literal 'mock' when VITE_TENANT_ID
  // is unset, which is not a real tenant name — comparing against it in a
  // misconfigured live build would mark the entire fleet foreign. Treat it
  // as "unconfigured" and defer to the payload's own receiving-tenant field.
  const configured = ownTenantId();
  const own = configured && configured !== 'mock' ? configured : text(p.tenantId);
  // No trustworthy "us" to compare against — say nothing rather than paint
  // the whole fleet as foreign.
  if (!own) return null;
  return norm(source) === norm(own) ? null : source;
}
